/**
 * Map-matching primitives. All distances in metres.
 *
 * We work in a local *flat* approximation around a reference latitude,
 * which keeps the math fast and is accurate to <1% across the kind of
 * radius we ever query (a few hundred metres).
 */

export type WayTags = {
  highway?: string;
  maxspeed?: string;
  name?: string;
  ref?: string;
  oneway?: string;
};

export type Way = {
  id: number;
  tags: WayTags;
  /** [lat, lon] pairs */
  geom: Array<[number, number]>;
};

export type Snap = {
  way: Way;
  /** Snapped lat/lon — closest point on the way to the input position. */
  lat: number;
  lon: number;
  /** Distance from input to snapped point, in metres. */
  dist: number;
  /** Bearing of the snapped segment, degrees clockwise from north. */
  bearing: number;
};

const METRES_PER_DEG_LAT = 111_320;
function metresPerDegLon(lat: number) {
  return 111_320 * Math.cos((lat * Math.PI) / 180);
}

export function haversine(aLat: number, aLon: number, bLat: number, bLon: number) {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/**
 * Nearest point on a single way (polyline) to a query lat/lon.
 * Returns local-flat-projection result with metre-accurate distance.
 */
function nearestOnWay(qLat: number, qLon: number, way: Way): Snap | null {
  if (way.geom.length < 2) return null;
  const mLat = METRES_PER_DEG_LAT;
  const mLon = metresPerDegLon(qLat);
  const qx = qLon * mLon;
  const qy = qLat * mLat;

  let bestDist2 = Infinity;
  let bestX = 0, bestY = 0;
  let bestBearing = 0;

  for (let i = 0; i < way.geom.length - 1; i++) {
    const [aLat, aLon] = way.geom[i];
    const [bLat, bLon] = way.geom[i + 1];
    const ax = aLon * mLon, ay = aLat * mLat;
    const bx = bLon * mLon, by = bLat * mLat;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) continue;
    let t = ((qx - ax) * dx + (qy - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const px = ax + t * dx;
    const py = ay + t * dy;
    const ddx = qx - px, ddy = qy - py;
    const d2 = ddx * ddx + ddy * ddy;
    if (d2 < bestDist2) {
      bestDist2 = d2;
      bestX = px;
      bestY = py;
      // bearing of segment: atan2(east, north) → degrees clockwise from north
      const east = dx;
      const north = dy;
      let bearing = Math.atan2(east, north) * (180 / Math.PI);
      if (bearing < 0) bearing += 360;
      bestBearing = bearing;
    }
  }
  if (!isFinite(bestDist2)) return null;
  return {
    way,
    lat: bestY / mLat,
    lon: bestX / mLon,
    dist: Math.sqrt(bestDist2),
    bearing: bestBearing,
  };
}

/**
 * Choose the best snap candidate among a set of ways.
 *
 * Strategy:
 *  - Compute nearest point on every way.
 *  - Skip ways farther than maxDistance.
 *  - Score = distance + heading penalty (if heading provided).
 *  - Hysteresis: if `stickyWayId` is one of the candidates AND its score
 *    is within `stickThreshold` of the best, keep it (avoids flicker
 *    between parallel ways like a divided carriageway).
 */
export function chooseSnap(
  qLat: number,
  qLon: number,
  ways: Way[],
  opts: {
    heading?: number | null;
    maxDistance?: number;
    stickyWayId?: number | null;
    stickThreshold?: number;
  } = {},
): Snap | null {
  const maxDistance = opts.maxDistance ?? 35;
  const headingWeight = 0.6; // metres of penalty per degree off
  const stickThreshold = opts.stickThreshold ?? 8;
  const heading = opts.heading;

  let best: Snap | null = null;
  let bestScore = Infinity;
  let stickyScore = Infinity;
  let stickySnap: Snap | null = null;

  for (const w of ways) {
    const s = nearestOnWay(qLat, qLon, w);
    if (!s || s.dist > maxDistance) continue;
    let score = s.dist;
    if (heading != null) {
      // 0..180 alignment delta (ways are bidirectional)
      const diff = Math.abs(((s.bearing - heading + 540) % 360) - 180);
      const offFromAlignment = Math.min(diff, 180 - diff); // 0..90
      // beyond 30° of misalignment, start to penalise
      const penalty = Math.max(0, offFromAlignment - 30) * headingWeight;
      score += penalty;
    }
    if (score < bestScore) {
      bestScore = score;
      best = s;
    }
    if (opts.stickyWayId != null && w.id === opts.stickyWayId) {
      stickyScore = score;
      stickySnap = s;
    }
  }
  // hysteresis — prefer previous way if it's almost as good
  if (stickySnap && stickyScore - bestScore < stickThreshold) return stickySnap;
  return best;
}

/** Parse OSM maxspeed tag → km/h or null. Handles "50", "50 mph", "30 kmh", "DK:rural" implicit values. */
export function parseMaxspeed(raw: string | undefined): number | null {
  if (!raw) return null;
  // implicit country codes — best-effort defaults
  const implicit: Record<string, number> = {
    "DK:urban": 50, "DK:rural": 80, "DK:motorway": 130,
    "DE:urban": 50, "DE:rural": 100, "DE:living_street": 7,
    "SE:urban": 50, "SE:rural": 70, "SE:motorway": 110,
    "NO:urban": 50, "NO:rural": 80, "NO:motorway": 100,
    "GB:nsl_single": 96, "GB:nsl_dual": 112, "GB:motorway": 112,
  };
  if (raw in implicit) return implicit[raw];
  const m = String(raw).match(/(\d+)\s*(mph|kmh|km\/h)?/i);
  if (!m) return null;
  const value = Number(m[1]);
  const unit = (m[2] || "").toLowerCase();
  return unit === "mph" ? Math.round(value * 1.60934) : value;
}
